import * as React from "react"
import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Star } from "lucide-react"
import { useAuthStore } from "@/store"
import {
  Sidebar,
  SidebarContent,
  SidebarRail,
  useSidebar,
} from "@repo/ui/primitives/sidebar"
import { useTheme } from "@/lib/theme.jsx"
import { SidebarHeaderSection } from "@/components/sidebar/SidebarHeaderSection"
import { SidebarSearch } from "@/components/sidebar/SidebarSearch"
import { SidebarSearchResults } from "@/components/sidebar/SidebarSearchResults"
import { SidebarMenuSection } from "@/components/sidebar/SidebarMenuSection"
import { SidebarFooterSection } from "@/components/sidebar/SidebarFooterSection"
import { SidebarWorkspaceSelector, resolveSidebarWorkspaceSelection, SidebarWorkspaceFilterCaption } from "@/components/sidebar/sidebar-workspace-selector.jsx"
import { useSidebarPreferencesStore } from "@/store/sidebar-preferences.store";
import {
  sidebarSubItemMatchesPath,
  sidebarSearchParamsMatch,
  sidebarPageMatchesQuery,
  collectOpenMenuKeysForPath,
  mergeOpenMenus,
  groupNavBySections,
  navBranchContainsPath,
} from "@/config/sidebar-nav.utils.js";
import { prefetchNamespacesForPath } from "@/lib/i18n-route-namespaces.js";
import { ensureSidebarLabelNamespaces } from "@/lib/i18n-sidebar-label-namespaces.js";
import { useSidebarScrollPosition } from "@/hooks/useSidebarScrollPosition.js";
import { Logger } from "@/lib/app-log.js";

function createFavoritesNavItem({ favoritePages, favoriteUrls, allPages, t }) {
  if (favoritePages.length > 0) {
    return {
      id: "navigation-favorites",
      url: "#",
      icon: Star,
      title: t("favorites.title", { defaultValue: "Favorites" }),
      submenuClassName: "max-h-64 overflow-y-auto",
      items: favoritePages.map((page) => ({
        ...page,
        id: page.url,
        isFavorite: favoriteUrls.has(page.url),
      })),
    };
  }

  if (allPages.some((page) => page.url === "/home")) {
    return {
      id: "navigation-favorites",
      url: "/home#favorites",
      icon: Star,
      title: t("favorites.emptyLink", {
        defaultValue: "Open Home to see your favorites.",
      }),
    };
  }

  return {
    id: "navigation-favorites",
    url: "#",
    icon: Star,
    title: t("workspace.empty", {
      defaultValue: "No accessible pages in this workspace.",
    }),
  };
}

function filterSidebarSearchResults(allPages, query) {
  return allPages.filter((page) => sidebarPageMatchesQuery(page, query));
}

export function SISMSidebar({
  data,
  allPages = [],
  availableModules = [],
  activeWorkspace = "all",
  customWorkspaces = [],
  favoritePages = [],
  favoriteUrls = new Set(),
  onSelectWorkspace,
  navigationReady = false,
  ...props
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar, state, setOpenMobile, isMobile } = useSidebar();
  const { user, status } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation(["sidebar", "header"]);
  const {
    accordionMode,
    density,
  } = useSidebarPreferencesStore();

  const workspaceSelection = React.useMemo(
    () => resolveSidebarWorkspaceSelection({
      modules: availableModules,
      customWorkspaces,
      activeWorkspace,
      t,
    }),
    [activeWorkspace, availableModules, customWorkspaces, t],
  );

  const navMain = React.useMemo(() => data?.navMain || [], [data?.navMain]);
  const navGroups = React.useMemo(
    () => groupNavBySections(data?.sections, navMain),
    [data?.sections, navMain],
  );
  const navGroupsWithFavorites = React.useMemo(() => {
    const favoriteItem = createFavoritesNavItem({
      favoritePages,
      favoriteUrls,
      allPages,
      t,
    });
    const groups = navGroups.map((group) => ({
      ...group,
      items: [...group.items],
    }));
    const quickAccessIndex = groups.findIndex((group) => group.id === "quickAccess");
    if (quickAccessIndex === -1) {
      return [{ id: "quickAccess", label: null, items: [favoriteItem] }, ...groups];
    }
    groups[quickAccessIndex].items.push(favoriteItem);
    return groups;
  }, [allPages, favoritePages, favoriteUrls, navGroups, t]);

  const navMainRef = useRef(navMain);
  navMainRef.current = navMain;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Ref for search input
  const searchInputRef = useRef(null);
  // Ref for search container to handle click outside
  const searchContainerRef = useRef(null);

  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearching(false);
      }
    };

    if (isSearching) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearching]);

  // State for tracking open menu items
  const [openMenus, setOpenMenus] = useState(() => {
    // Don't load saved state if user is not authenticated
    if (!user) {
      return {};
    }

    try {
      const saved = localStorage.getItem('sidebar-open-menus');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const isCollapsed = !isMobile && state === "collapsed";

  const clearOpenMenusIfNeeded = React.useCallback(() => {
    setOpenMenus((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, []);

  // Save open menus to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('sidebar-open-menus', JSON.stringify(openMenus));
    } catch (error) {
      Logger.error('Failed to save sidebar state:', { error: error?.message });
    }
  }, [openMenus]);

  const openMenuSignature = React.useMemo(
    () => Object.keys(openMenus).filter((key) => openMenus[key]).sort().join(","),
    [openMenus],
  );
  const navSignature = React.useMemo(
    () => navMain.map((item) => item.id).join("|"),
    [navMain],
  );
  const {
    setSidebarContentRef,
    saveScrollPosition,
    clearScrollPosition,
  } = useSidebarScrollPosition({
    enabled: Boolean(user) && !isCollapsed,
    restoreEpoch: [
      location.pathname,
      navigationReady,
      navSignature,
      favoritePages.length,
      openMenuSignature,
    ].join(":"),
  });

  // Clear open menus when user logs out or changes
  useEffect(() => {
    if (!user || status === "unauthenticated") {
      clearOpenMenusIfNeeded();
      clearScrollPosition();
      try {
        localStorage.removeItem('sidebar-open-menus');
      } catch (error) {
        Logger.error('Failed to clear sidebar state:', { error: error?.message });
      }
    }
  }, [user, status, clearOpenMenusIfNeeded, clearScrollPosition]);

  // Auto-open menus based on active path (stable callback — nav read from ref)
  const openMenusForActivePath = React.useCallback(
    (currentPath) => collectOpenMenuKeysForPath(navMainRef.current, currentPath),
    [],
  );

  // Clear menus when sidebar is collapsed
  useEffect(() => {
    if (!isCollapsed) return;
    clearOpenMenusIfNeeded();
  }, [isCollapsed, clearOpenMenusIfNeeded]);

  // Auto-open menus for the active route when sidebar is expanded
  useEffect(() => {
    if (isCollapsed || !user) return;

    const menusToOpen = openMenusForActivePath(location.pathname);
    if (Object.keys(menusToOpen).length === 0) return;

    setOpenMenus((prevOpenMenus) => mergeOpenMenus(prevOpenMenus, menusToOpen));
  }, [location.pathname, isCollapsed, user, openMenusForActivePath]);

  // Toggle main menu
  const toggleMainMenu = (menuKey) => {
    // Save scroll position before menu change
    saveScrollPosition();
    setOpenMenus(prev => {
      const isOpening = !prev[menuKey];
      if (isOpening && accordionMode) {
        // Close all other main menus, but keep sub menus of other parents?
        // Usually accordion means only one main menu open at a time.
        const newOpenMenus = {};
        newOpenMenus[menuKey] = true;
        return newOpenMenus;
      }
      return {
        ...prev,
        [menuKey]: isOpening
      };
    });
  };

  // Toggle sub menu
  const toggleSubMenu = (parentKey, subKey) => {
    const fullKey = `${parentKey}-${subKey}`;
    // Save scroll position before menu change
    saveScrollPosition();
    setOpenMenus(prev => {
      const isOpening = !prev[fullKey];
      if (isOpening && accordionMode) {
        // Close other submenus of the SAME parent
        const newOpenMenus = { ...prev };
        Object.keys(newOpenMenus).forEach(key => {
          if (key.startsWith(`${parentKey}-`) && key !== fullKey) {
            newOpenMenus[key] = false;
          }
        });
        newOpenMenus[fullKey] = true;
        return newOpenMenus;
      }
      return {
        ...prev,
        [fullKey]: isOpening
      };
    });
  };

  // Check if menu is open
  const isMenuOpen = (menuKey) => {
    return openMenus[menuKey] || false;
  };

  // Check if sub menu is open
  const isSubMenuOpen = (parentKey, subKey) => {
    const fullKey = `${parentKey}-${subKey}`;
    return openMenus[fullKey] || false;
  };

  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setSearchResults([]);
  };

  const handleSearch = (query) => {
    if (!query) {
      clearSearch();
      return;
    }

    void ensureSidebarLabelNamespaces();
    setSearchQuery(query);
    setIsSearching(true);
    setSearchResults(filterSidebarSearchResults(allPages, query));
  };

  useEffect(() => {
    if (searchQuery.length > 0) {
      setSearchResults(filterSidebarSearchResults(allPages, searchQuery));
    }
  }, [allPages, searchQuery]);

  const handleSearchFocus = () => {
    void ensureSidebarLabelNamespaces();
  };

  const handleSearchClick = () => {
    void ensureSidebarLabelNamespaces();
    if (searchQuery.length > 0) {
      setIsSearching(true);
    }
  };

  const handlePageClick = (url) => {
    saveScrollPosition();
    if (url !== "#") {
      void prefetchNamespacesForPath(url);
    }
    clearSearch();
    // Leaf navigation dismisses the mobile sheet; desktop collapse state is preserved.
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleSearchResultSelect = () => {
    clearSearch();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const isActive = (
    url,
    exact = false,
    activePathPrefixes,
    excludePathPrefixes,
    activeSearchParams,
    activeSearchParamsAllowMissing = false,
  ) => {
    if (!sidebarSearchParamsMatch(location.search, activeSearchParams, activeSearchParamsAllowMissing)) {
      return false;
    }

    return sidebarSubItemMatchesPath(location.pathname, {
      url,
      exact,
      activePathPrefixes,
      excludePathPrefixes,
    });
  };

  // Find which parent menu contains the current path
  const findParentMenuForPath = (currentPath) => {
    for (const mainItem of navMain) {
      // Check if current path matches main item directly
      if (mainItem.url && mainItem.url !== "#" && (currentPath === mainItem.url || currentPath.startsWith(mainItem.url + "/"))) {
        return mainItem;
      }

      // Check sub-items (2nd level)
      if (mainItem.items) {
        for (const subItem of mainItem.items) {
          if (sidebarSubItemMatchesPath(currentPath, subItem)) {
            return mainItem;
          }

          // Check sub-sub-items (3rd level)
          if (subItem.items) {
            for (const subSubItem of subItem.items) {
              if (sidebarSubItemMatchesPath(currentPath, subSubItem)) {
                return mainItem;
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Expanded: parent highlights only when accordion is closed but a descendant is active.
  // Icon rail: parent highlights whenever a descendant route is active.
  const isParentActive = (mainItem) => {
    if (isCollapsed) {
      const parentMenu = findParentMenuForPath(location.pathname);
      return Boolean(parentMenu && parentMenu.id === mainItem.id);
    }

    if (!Array.isArray(mainItem.items) || mainItem.items.length === 0) {
      return isActive(mainItem.url, mainItem.exact, mainItem.activePathPrefixes, mainItem.excludePathPrefixes);
    }

    const hasActiveDescendant = findParentMenuForPath(location.pathname)?.id === mainItem.id;
    if (hasActiveDescendant && !isMenuOpen(mainItem.id)) {
      return true;
    }

    return isActive(mainItem.url, mainItem.exact, mainItem.activePathPrefixes, mainItem.excludePathPrefixes);
  };

  const isSubBranchActive = (parentId, subItem) => {
    if (!navBranchContainsPath(location.pathname, subItem)) return false;
    if (isSubMenuOpen(parentId, subItem.id)) {
      return isActive(subItem.url, subItem.exact, subItem.activePathPrefixes, subItem.excludePathPrefixes);
    }
    return true;
  };


  return (
    <Sidebar
      collapsible="icon"
      data-helper="sidebar"
      data-density={density}
      {...props}
    >
      {/* Modern Header */}
      <SidebarHeaderSection isCollapsed={isCollapsed} navigate={navigate} />

      <div
        className="shrink-0 border-b border-sidebar-border bg-sidebar"
        data-sidebar="nav-toolbar"
      >
        {isCollapsed ? (
          <SidebarWorkspaceSelector
            modules={availableModules}
            customWorkspaces={customWorkspaces}
            activeWorkspace={activeWorkspace}
            onSelectWorkspace={onSelectWorkspace}
            isCollapsed
            disabled={!navigationReady}
          />
        ) : (
          <div className="relative px-4 pb-2 pt-3" ref={searchContainerRef}>
            <SidebarSearch
              searchQuery={searchQuery}
              onChange={handleSearch}
              onClear={clearSearch}
              searchInputRef={searchInputRef}
              onInputClick={handleSearchClick}
              onFocus={handleSearchFocus}
              trailing={(
                <SidebarWorkspaceSelector
                  modules={availableModules}
                  customWorkspaces={customWorkspaces}
                  activeWorkspace={activeWorkspace}
                  onSelectWorkspace={onSelectWorkspace}
                  disabled={!navigationReady}
                />
              )}
            />

            {isSearching && searchResults.length > 0 && (
              <SidebarSearchResults
                searchResults={searchResults}
                onResultSelect={handleSearchResultSelect}
              />
            )}
          </div>
        )}
      </div>

      {!isCollapsed && workspaceSelection.isFiltered ? (
        <div
          className="shrink-0 bg-sidebar px-4 pb-1.5 pt-2"
          data-sidebar="filter-caption"
        >
          <SidebarWorkspaceFilterCaption
            label={workspaceSelection.selectedLabel}
            isFiltered
          />
        </div>
      ) : null}

      <SidebarContent
        ref={setSidebarContentRef}
        data-sidebar="content"
        className="custom-scrollbar gap-0 overflow-y-auto scroll-auto [overflow-anchor:none]"
      >
        <div data-helper="sidebar-main-tabs" className={isCollapsed ? undefined : "px-4"}>
          <SidebarMenuSection
            navGroups={navGroupsWithFavorites}
            isCollapsed={isCollapsed}
            navigate={navigate}
            toggleSidebar={toggleSidebar}
            isMenuOpen={isMenuOpen}
            toggleMainMenu={toggleMainMenu}
            isSubMenuOpen={isSubMenuOpen}
            toggleSubMenu={toggleSubMenu}
            isParentActive={isParentActive}
            isSubBranchActive={isSubBranchActive}
            isActive={isActive}
            handlePageClick={handlePageClick}
          />
        </div>
      </SidebarContent>

      {/* Modern Footer */}
      <SidebarFooterSection
        isCollapsed={isCollapsed}
        theme={theme}
        toggleTheme={toggleTheme}
        toggleSidebar={toggleSidebar}
      />

      <SidebarRail />
    </Sidebar>
  )
}

